'use client';
import { useContext, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { useTranslations } from 'next-intl';
import Head from 'next/head';
import Link from 'next/link';

import CarouselSection from '../../components/CarouselSection';
import Footer from '../../components/Footer';
import GenreItem from '../../components/GenreItem';
import Loading from '../../components/Loading';
import LoadingContainer from '../../components/LoadingContainer';
import ResourceItem from '../../components/ResourceItem';
import WatchProviderComponent from '../../components/WatchProvider';
import Wrapper from '../../components/Wrapper';
import { amplitudeInstance } from '../../config/amplitude';
import env from '../../config/env';
import AuthContext from '../../context/AuthContext';
import buildRouteUrl from '../../helpers/buildRouteUrl';
import useIntl from '../../hooks/useIntl';
import api from '../../services/api';
import { CatalogSection, Genre, WatchProvider } from '../../types/types';

interface PropTypes {
  browse: any;
  genres: any;
}

function ClientBrowse({ browse, genres }: PropTypes) {
  const queryClient = useQueryClient();
  const auth = useContext(AuthContext);
  const intl = useIntl();

  const t = useTranslations();

  const genresQuery = useQuery<{
    data: Genre[];
  }>(
    'genres',
    async () => {
      const response = await api.get('/v1/genres');

      return response.data;
    },
    {
      cacheTime: 100000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: 100000,
      initialData: genres
    }
  );

  useEffect(() => {
    if (auth.isLoggedIn) {
      queryClient.prefetchQuery('user-tv-to-watch-next-all', async () => {
        const response = await api.get('/v1/me/tv-to-watch-next', {
          params: {
            filter: 'all'
          }
        });

        return response.data;
      });
      queryClient.prefetchQuery('user-tv-to-watch-next-watching', async () => {
        const response = await api.get('/v1/me/tv-to-watch-next', {
          params: {
            filter: 'watching'
          }
        });

        return response.data;
      });
      queryClient.prefetchQuery(
        'user-tv-to-watch-next-not-started',
        async () => {
          const response = await api.get('/v1/me/tv-to-watch-next', {
            params: {
              filter: 'not_started'
            }
          });

          return response.data;
        }
      );
      queryClient.prefetchQuery(
        'user-tv-to-watch-next-stopped-watching',
        async () => {
          const response = await api.get('/v1/me/tv-to-watch-next', {
            params: {
              filter: 'stopped_watching'
            }
          });

          return response.data;
        }
      );
    }
  }, [auth.isLoggedIn]);

  const catalogQuery = useQuery<{ data: CatalogSection[] }>(
    ['catalog', intl.locale],
    async () => {
      const response = await api.get('/v1/browse', {
        params: {
          region: intl.locale
        },
        headers: {
          'accept-language': intl.full_language
        }
      });

      return response.data;
    },
    {
      cacheTime: 100000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: 100000,
      initialData: browse
    }
  );

  const isLoading = genresQuery.isLoading || catalogQuery.isLoading;

  return (
    <Wrapper>
      <Head>
        <meta
          key="og:image"
          name="og:image"
          content={'https://thecinehub.app/assets/og-preview.jpg'}
        />
        <link rel="canonical" href={`${env.WEBAPP_URL}/`} />
      </Head>
      {isLoading && (
        <LoadingContainer>
          <Loading />
        </LoadingContainer>
      )}

      {!isLoading && (
        <div className="flex flex-col px-4 md:px-10 w-full bg-gray-600 min-h-[calc(100vh-64px-60px)]">
          {(genresQuery.data?.data.length || 0) > 0 && (
            <CarouselSection
              id="genres"
              title={t('home.genres')}
              type="genre"
              totalItems={genresQuery.data?.data.length || 0}
              className="mt-3"
            >
              {genresQuery.data?.data.map(genre => (
                <div key={genre.id} className="embla__slide">
                  <Link
                    prefetch={false}
                    href={buildRouteUrl(`/genres/${genre.id}/movies`)}
                    onClick={() => {
                      amplitudeInstance.logEvent(
                        'Go To Genre Contents Page From Catalog List',
                        {
                          genre_id: genre.id,
                          auto_generated: true
                        }
                      );
                    }}
                  >
                    <GenreItem className="mr-2" data={genre} />
                  </Link>
                </div>
              ))}
            </CarouselSection>
          )}

          {(catalogQuery.data?.data.length || 0) > 0 &&
            catalogQuery.data?.data.map(section => {
              if (section.type === 'content') {
                return (
                  <CarouselSection
                    id={section.slug}
                    key={section.title}
                    title={section.title}
                    type="resource"
                    watchProvider={section.watch_provider}
                    totalItems={section.data.length}
                    className="mt-6"
                  >
                    {section.data.map(item => {
                      const url = buildRouteUrl(`/${item.type}/${item._id}`);
                      return (
                        <div key={item._id} className="embla__slide">
                          <Link href={url}>
                            <ResourceItem
                              className="mr-2"
                              data={item}
                              onClick={() => {
                                amplitudeInstance.logEvent(
                                  'Go To Content Page From Catalog List',
                                  {
                                    content_id: item._id,
                                    content_type: item.type,
                                    catalog_list_id: section._id,
                                    catalog_list_slug: section.slug,
                                    auto_generated: section.auto_generated
                                  }
                                );
                              }}
                            />
                          </Link>
                        </div>
                      );
                    })}
                  </CarouselSection>
                );
              }

              if (section.type === 'watch_providers') {
                return (
                  <CarouselSection
                    id="watch_providers"
                    key={section.title}
                    title={section.title}
                    type="watch_provider"
                    totalItems={section.data.length}
                    className="mt-6"
                  >
                    {(section.data as unknown as WatchProvider[]).map(item => (
                      <div key={item._id} className="embla__slide">
                        <Link
                          prefetch={false}
                          href={buildRouteUrl(
                            `/watch-providers/${item.slug}/all`
                          )}
                          onClick={() => {
                            amplitudeInstance.logEvent(
                              'Go To Watch Provider Contents Page From Catalog List',
                              {
                                watch_provider_name: item.name,
                                watch_provider_slug: item.slug,
                                watch_provider_id: item._id
                              }
                            );
                          }}
                        >
                          <WatchProviderComponent
                            className="mr-2"
                            data={item}
                            alt={item.name}
                          />
                        </Link>
                      </div>
                    ))}
                  </CarouselSection>
                );
              }
            })}
        </div>
      )}

      <Footer />
    </Wrapper>
  );
}

export default ClientBrowse;
